import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MEETING_UPDATE_OPTION } from 'src/content/MiniExperts/SmartMeet/constants/constants';
import type {
  CreateMeetingOptions,
  MeetingData,
  MeetingListItem
} from 'src/content/MiniExperts/SmartMeet/types/types';
import { getFormattedTime } from 'src/content/MiniExperts/SmartMeet/utils/utils';
import { useAlert } from 'src/hooks/useAlert';
import useLazyQuery from 'src/hooks/useLazyQuery';
import useMutation from 'src/hooks/useMutation';
import { updateSmartMeetCount } from 'src/redux/slices/data';
import {
  RecordingProgress,
  setMeetingData,
  setProgress
} from 'src/redux/slices/smartMeet';
import { useDispatch, useSelector } from 'src/redux/store';
import {
  useCreateMeetingMutation,
  useDeleteMeetingMutation,
  useLazyGetExistingMeetingsQuery,
  useUpdateMeetingMutation
} from 'src/services/api';
import { ALERT } from 'src/types/enum';
import { ErrorContext } from 'src/utils/errorMappings';
import logger from 'src/utils/logger';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

export const useSmartMeetService = () => {
  const pageSize = 10;
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  const [fetchExistingMeetings, { isLoading }] = useLazyQuery<
    unknown,
    { data: MeetingData[] }
  >({
    api: useLazyGetExistingMeetingsQuery
  });

  const { notes, seconds, currentPage } = useSelector(
    (state) => state.smartMeet
  );
  const [totalMeetingCount, setTotalMeetingCount] = useState<number>(0);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const [createMeeting] = useCreateMeetingMutation();
  const [deleteMeeting] = useDeleteMeetingMutation();
  const [updateMeeting] = useMutation({
    api: useUpdateMeetingMutation,
    errorContext: ErrorContext.SMART_MEET
  });

  const createNewMeeting = async (
    title: string,
    options: CreateMeetingOptions = {}
  ) => {
    const { rid, type_id, add_notes = true, duration } = options;
    try {
      const time = getFormattedTime(duration ?? seconds);

      dispatch(
        setProgress({
          status_code: RecordingProgress.GeneratingData
        })
      );
      const notesToSend = add_notes ? notes : [];

      const data = await createMeeting({
        params: {
          name: title,
          notes: JSON.stringify({ items: notesToSend }),
          duration: time,
          ...(rid &&
            type_id && { rid_list: JSON.stringify([{ rid, type_id }]) })
        }
      }).unwrap();

      const meetingData = data?.data?.data?.[0];
      const ConsumedSmartMeetSeconds =
        data?.data?.total_limit_seconds ?? meetingData?.total_limit_seconds;
      if (
        ConsumedSmartMeetSeconds !== undefined &&
        ConsumedSmartMeetSeconds !== null
      ) {
        dispatch(updateSmartMeetCount(ConsumedSmartMeetSeconds));
      }
      dispatch(setMeetingData(meetingData));
      return data;
    } catch (error) {
      logger.error('Error creating meeting:', error);
      showAlert(ALERT.ERROR, `${t(T.failedToCreate)}${error?.error || ''}`);
      dispatch(setProgress(null));
      throw error;
    }
  };

  const mapMeetings = (meetings: MeetingData[]): MeetingListItem[] =>
    meetings.map((m) => {
      const created = new Date(m.created_on);
      const modified = new Date(m.modified_on);
      return {
        id: m?.id,
        name: m?.name,
        date: created.toISOString(),
        time: created.toISOString(),
        duration: String(m?.settings?.duration ?? ''),
        organizer: m?.username || m?.created_by,
        fileName: m?.resources?.[0]?.file?.name ?? '',
        status_code: m?.status_code ?? '',
        tenant_id: m?.tenant_id,
        permission: m?.permissions?.[0]?.role ?? '',
        state: m?.state ?? false,
        status_details: m?.status_details,
        modified_on: modified.toISOString()
      };
    });

  const fetchMeetings = async ({
    pageSize
  }: {
    pageSize: number;
  }): Promise<{
    meetings: MeetingListItem[];
    totalCount: number;
  }> => {
    setIsFetching(true);

    return new Promise((resolve, reject) => {
      fetchExistingMeetings({
        params: { params: { ps: pageSize, p: currentPage, t: Date.now() } },
        fallbackMsg: T.uploadDocFailed,

        successCallback: (
          response: { total_count?: number; data?: MeetingData[] } | undefined
        ) => {
          const totalCount = response?.total_count ?? 0;
          const meetings = mapMeetings(response?.data ?? []);

          setTotalMeetingCount(totalCount);
          setIsFetching(false);

          resolve({
            meetings: meetings || [],
            totalCount
          });
        },

        errorCallback: (error) => {
          setIsFetching(false);
          logger.error('Error fetching meetings:', error);
          showAlert(ALERT.ERROR, t(T.failedFetchingMeetings));
          reject(error);
        }
      });
    });
  };

  const deleteSmartMeet = async (meetingId: number) => {
    try {
      await deleteMeeting({
        params: { id: meetingId }
      }).unwrap();
      showAlert(ALERT.SUCCESS, t(T.deleteMeetingSuccess));
    } catch (error) {
      showAlert(ALERT.ERROR, t(T.deleteMeetingError));
      throw error;
    }
  };

  const getMeetingData = async (
    meetingId: number
  ): Promise<MeetingData | undefined> => {
    return new Promise((resolve) => {
      fetchExistingMeetings({
        params: { params: { id: meetingId, t: Date.now() } },
        fallbackMsg: T.uploadDocFailed,
        successCallback: (_) => {
          const meeting = _?.data?.[0];
          dispatch(setMeetingData(meeting));
          resolve(meeting);
        },
        errorCallback: (_) => {
          logger.error('Error fetching meeting data:', _);
          showAlert(ALERT.ERROR, t(T.noExistingMeeting));
          resolve(undefined);
        }
      });
    });
  };

  const updateSmartMeet = async (meetingId: number, params: any) => {
    try {
      if (!params?.op) {
        showAlert(ALERT.SUCCESS, t(T.updatingMeeting));
      }
      const data = await updateMeeting({
        params: {
          meetingId,
          params: {
            op: MEETING_UPDATE_OPTION.UPDATE,
            ...params
          }
        },
        fallbackMsg: T.failedToUpdate
      });
      const meetingData = data?.data?.data[0];
      const ConsumedSmartMeetSeconds = data?.total_limit_seconds;
      if (
        ConsumedSmartMeetSeconds !== undefined &&
        ConsumedSmartMeetSeconds !== null
      ) {
        dispatch(updateSmartMeetCount(ConsumedSmartMeetSeconds));
      }
      return meetingData;
    } catch (error) {
      logger.error('Error updating meeting:', error);
      throw error;
    }
  };

  return {
    pageSize,
    isLoading,
    isFetching,
    totalMeetingCount,
    fetchMeetings,
    createNewMeeting,
    deleteSmartMeet,
    updateSmartMeet,
    getMeetingData
  };
};
